import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface SharedDashboard {
  id: string
  name: string
  description?: string
  dashboard_config: {
    widgets: Array<{ id: string; type: string; title: string }>
    layout: string
    theme: string
  }
  owner_id: string
  visibility: 'private' | 'link' | 'public'
  permissions: Array<{ user_id: string; role: string; granted_at: string }>
  share_token: string
  embed_enabled: boolean
  embed_settings?: Record<string, any>
  password_protected: boolean
  expiry_date?: string
  view_count: number
  last_viewed?: string
  created_at: string
  updated_at: string
}

interface EmbedCode {
  embed_token: string
  iframe_url: string
  iframe_code: string
  script_code: string
  settings: Record<string, any>
}

interface AccessLog {
  id: string
  dashboard_id: string
  access_type: string
  user_id?: string
  ip_address?: string
  user_agent?: string
  referrer?: string
  timestamp: string
}

interface Analytics {
  dashboard_id: string
  total_views: number
  recent_views: number
  embed_views: number
  unique_users: number
  unique_visitors: number
  views_by_day: Record<string, number>
  last_viewed?: string
  permissions_count: number
}

const API_BASE = '/api'

export default function DashboardSharingPage() {
  const [activeTab, setActiveTab] = useState<'dashboards' | 'create' | 'analytics'>('dashboards')
  const [dashboards, setDashboards] = useState<SharedDashboard[]>([])
  const [selectedDashboard, setSelectedDashboard] = useState<SharedDashboard | null>(null)
  const [embedCode, setEmbedCode] = useState<EmbedCode | null>(null)
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([])
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showEmbedModal, setShowEmbedModal] = useState(false)
  const [showPermissionModal, setShowPermissionModal] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    visibility: 'private',
    embed_enabled: false,
    password: '',
    expiry_days: ''
  })

  const [permissionForm, setPermissionForm] = useState({
    user_id: '',
    role: 'viewer'
  })

  useEffect(() => {
    fetchDashboards()
  }, [])

  const fetchDashboards = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE}/sharing/dashboards`)
      const data = await response.json()
      setDashboards(data.dashboards || [])
    } catch (error) {
      console.error('Error fetching dashboards:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDashboardDetails = async (dashboardId: string) => {
    try {
      const [dashboardRes, analyticsRes, logsRes] = await Promise.all([
        fetch(`${API_BASE}/sharing/dashboards/${dashboardId}`),
        fetch(`${API_BASE}/sharing/dashboards/${dashboardId}/analytics`),
        fetch(`${API_BASE}/sharing/dashboards/${dashboardId}/logs?limit=50`)
      ])

      const dashboard = await dashboardRes.json()
      const analyticsData = await analyticsRes.json()
      const logsData = await logsRes.json()

      setSelectedDashboard(dashboard)
      setAnalytics(analyticsData)
      setAccessLogs(logsData.logs || [])
    } catch (error) {
      console.error('Error fetching dashboard details:', error)
    }
  }

  const createDashboard = async () => {
    try {
      const response = await fetch(`${API_BASE}/sharing/dashboards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createForm.name,
          description: createForm.description,
          dashboard_config: {
            widgets: [
              { id: 'w1', type: 'chart', title: 'Sample Chart', config: {} }
            ],
            layout: 'grid',
            theme: 'light'
          },
          visibility: createForm.visibility,
          embed_enabled: createForm.embed_enabled,
          password: createForm.password || null,
          expiry_days: createForm.expiry_days ? parseInt(createForm.expiry_days) : null
        })
      })

      if (response.ok) {
        setCreateForm({
          name: '',
          description: '',
          visibility: 'private',
          embed_enabled: false,
          password: '',
          expiry_days: ''
        })
        fetchDashboards()
        setActiveTab('dashboards')
      }
    } catch (error) {
      console.error('Error creating dashboard:', error)
    }
  }

  const generateEmbedCode = async (dashboardId: string) => {
    try {
      const response = await fetch(`${API_BASE}/sharing/dashboards/${dashboardId}/embed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          width: '100%',
          height: '600',
          theme: 'light'
        })
      })

      if (response.ok) {
        const data = await response.json()
        setEmbedCode(data)
        setShowEmbedModal(true)
      }
    } catch (error) {
      console.error('Error generating embed code:', error)
    }
  }

  const regenerateShareLink = async (dashboardId: string) => {
    try {
      const response = await fetch(`${API_BASE}/sharing/dashboards/${dashboardId}/regenerate-link`, {
        method: 'POST'
      })

      if (response.ok) {
        fetchDashboards()
        if (selectedDashboard?.id === dashboardId) {
          fetchDashboardDetails(dashboardId)
        }
      }
    } catch (error) {
      console.error('Error regenerating link:', error)
    }
  }

  const addPermission = async () => {
    if (!selectedDashboard) return

    try {
      const response = await fetch(`${API_BASE}/sharing/dashboards/${selectedDashboard.id}/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(permissionForm)
      })

      if (response.ok) {
        setPermissionForm({ user_id: '', role: 'viewer' })
        setShowPermissionModal(false)
        fetchDashboardDetails(selectedDashboard.id)
      }
    } catch (error) {
      console.error('Error adding permission:', error)
    }
  }

  const removePermission = async (userId: string) => {
    if (!selectedDashboard) return

    try {
      await fetch(`${API_BASE}/sharing/dashboards/${selectedDashboard.id}/permissions/${userId}`, {
        method: 'DELETE'
      })
      fetchDashboardDetails(selectedDashboard.id)
    } catch (error) {
      console.error('Error removing permission:', error)
    }
  }

  const deleteDashboard = async (dashboardId: string) => {
    if (!confirm('Are you sure you want to delete this shared dashboard?')) return

    try {
      await fetch(`${API_BASE}/sharing/dashboards/${dashboardId}`, {
        method: 'DELETE'
      })
      fetchDashboards()
      if (selectedDashboard?.id === dashboardId) {
        setSelectedDashboard(null)
      }
    } catch (error) {
      console.error('Error deleting dashboard:', error)
    }
  }

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (error) {
      console.error('Copy failed:', error)
    }
  }

  const getVisibilityBadgeClass = (visibility: string) => {
    switch (visibility) {
      case 'public': return 'badge-success'
      case 'link': return 'badge-info'
      case 'private': return 'badge-warning'
      default: return 'badge-secondary'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderDashboardsList = () => (
    <div className="grid gap-4">
      {loading ? (
        <div className="card p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading shared dashboards...</p>
        </div>
      ) : dashboards.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-500">No shared dashboards yet.</p>
          <button
            onClick={() => setActiveTab('create')}
            className="btn btn-primary mt-4"
          >
            Create Your First Shared Dashboard
          </button>
        </div>
      ) : (
        dashboards.map((dashboard) => (
          <div key={dashboard.id} className="card p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">{dashboard.name}</h3>
                  <span className={`badge ${getVisibilityBadgeClass(dashboard.visibility)}`}>
                    {dashboard.visibility}
                  </span>
                  {dashboard.password_protected && (
                    <span className="badge badge-secondary">Password Protected</span>
                  )}
                  {dashboard.embed_enabled && (
                    <span className="badge badge-info">Embed Enabled</span>
                  )}
                </div>
                {dashboard.description && (
                  <p className="text-gray-600 mt-1">{dashboard.description}</p>
                )}
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                  <span>Views: {dashboard.view_count}</span>
                  <span>Widgets: {dashboard.dashboard_config?.widgets?.length || 0}</span>
                  <span>Created: {formatDate(dashboard.created_at)}</span>
                  {dashboard.expiry_date && (
                    <span className="text-orange-500">Expires: {formatDate(dashboard.expiry_date)}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    fetchDashboardDetails(dashboard.id)
                    setShowShareModal(true)
                  }}
                  className="btn btn-sm btn-outline"
                >
                  Share
                </button>
                {dashboard.embed_enabled && (
                  <button
                    onClick={() => {
                      setSelectedDashboard(dashboard)
                      generateEmbedCode(dashboard.id)
                    }}
                    className="btn btn-sm btn-outline"
                  >
                    Embed
                  </button>
                )}
                <button
                  onClick={() => deleteDashboard(dashboard.id)}
                  className="btn btn-sm btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  )

  const renderCreateForm = () => (
    <div className="card p-6 max-w-2xl">
      <h3 className="text-lg font-semibold mb-6">Create Shared Dashboard</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Dashboard Name *</label>
          <input
            type="text"
            className="input w-full"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            placeholder="e.g., Q1 Sales Dashboard"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            className="input w-full"
            rows={3}
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            placeholder="Brief description of the dashboard"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Visibility</label>
          <select
            className="input w-full"
            value={createForm.visibility}
            onChange={(e) => setCreateForm({ ...createForm, visibility: e.target.value })}
          >
            <option value="private">Private - Only permitted users</option>
            <option value="link">Link Only - Anyone with the link</option>
            <option value="public">Public - Discoverable by anyone</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="embed_enabled"
            checked={createForm.embed_enabled}
            onChange={(e) => setCreateForm({ ...createForm, embed_enabled: e.target.checked })}
          />
          <label htmlFor="embed_enabled" className="text-sm">Enable embedding on external websites</label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Password Protection (optional)</label>
          <input
            type="password"
            className="input w-full"
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            placeholder="Leave empty for no password"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Expiry (days)</label>
          <input
            type="number"
            className="input w-full"
            value={createForm.expiry_days}
            onChange={(e) => setCreateForm({ ...createForm, expiry_days: e.target.value })}
            placeholder="Leave empty for no expiry"
            min="1"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button onClick={createDashboard} className="btn btn-primary" disabled={!createForm.name}>
            Create Shared Dashboard
          </button>
          <button onClick={() => setActiveTab('dashboards')} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

  const renderAnalyticsView = () => (
    <div className="space-y-6">
      {selectedDashboard && analytics ? (
        <>
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">{selectedDashboard.name} - Analytics</h3>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{analytics.total_views}</div>
                <div className="text-sm text-gray-600">Total Views</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{analytics.unique_visitors}</div>
                <div className="text-sm text-gray-600">Unique Visitors</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{analytics.embed_views}</div>
                <div className="text-sm text-gray-600">Embed Views</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{analytics.permissions_count}</div>
                <div className="text-sm text-gray-600">Shared With</div>
              </div>
            </div>

            <h4 className="font-medium mb-3">Views by Day (Last 7 Days)</h4>
            <div className="flex items-end gap-2 h-32">
              {Object.entries(analytics.views_by_day).map(([day, count]) => (
                <div key={day} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-blue-500 rounded-t"
                    style={{ height: `${Math.max(count * 20, 4)}px` }}
                  ></div>
                  <span className="text-xs mt-1">{new Date(day).toLocaleDateString('en', { weekday: 'short' })}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Access Logs</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Time</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">User</th>
                    <th className="text-left py-2">IP Address</th>
                    <th className="text-left py-2">Referrer</th>
                  </tr>
                </thead>
                <tbody>
                  {accessLogs.map((log) => (
                    <tr key={log.id} className="border-b">
                      <td className="py-2 text-sm">{formatDate(log.timestamp)}</td>
                      <td className="py-2">
                        <span className={`badge ${log.access_type === 'embed' ? 'badge-info' : 'badge-success'}`}>
                          {log.access_type}
                        </span>
                      </td>
                      <td className="py-2 text-sm">{log.user_id || 'Anonymous'}</td>
                      <td className="py-2 text-sm font-mono">{log.ip_address || '-'}</td>
                      <td className="py-2 text-sm truncate max-w-[200px]">{log.referrer || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="card p-8 text-center">
          <p className="text-gray-500">Select a dashboard to view analytics</p>
          <div className="mt-4 grid grid-cols-3 gap-4">
            {dashboards.slice(0, 3).map((dashboard) => (
              <button
                key={dashboard.id}
                onClick={() => fetchDashboardDetails(dashboard.id)}
                className="card p-4 text-left hover:bg-gray-50"
              >
                <div className="font-medium">{dashboard.name}</div>
                <div className="text-sm text-gray-500">Views: {dashboard.view_count}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Dashboard Sharing</h1>
            <p className="text-gray-600">Share dashboards and generate embed codes</p>
          </div>
          <button onClick={() => setActiveTab('create')} className="btn btn-primary">
            Share New Dashboard
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          <button
            onClick={() => setActiveTab('dashboards')}
            className={`pb-2 px-1 ${activeTab === 'dashboards' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          >
            Shared Dashboards
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`pb-2 px-1 ${activeTab === 'create' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          >
            Create New
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-2 px-1 ${activeTab === 'analytics' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          >
            Analytics
          </button>
        </div>

        {/* Content */}
        {activeTab === 'dashboards' && renderDashboardsList()}
        {activeTab === 'create' && renderCreateForm()}
        {activeTab === 'analytics' && renderAnalyticsView()}
      </main>

      {/* Share Modal */}
      {showShareModal && selectedDashboard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Share Dashboard</h3>
                <button onClick={() => setShowShareModal(false)} className="text-gray-500 hover:text-gray-700">
                  X
                </button>
              </div>

              <div className="space-y-4">
                {/* Share Link */}
                <div>
                  <label className="block text-sm font-medium mb-2">Share Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      className="input flex-1 bg-gray-50"
                      value={`http://192.168.0.98:8090/shared/${selectedDashboard.share_token}`}
                    />
                    <button
                      onClick={() => copyToClipboard(`http://192.168.0.98:8090/shared/${selectedDashboard.share_token}`, 'link')}
                      className="btn btn-outline"
                    >
                      {copiedField === 'link' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <button
                    onClick={() => regenerateShareLink(selectedDashboard.id)}
                    className="text-sm text-blue-600 mt-2 hover:underline"
                  >
                    Regenerate Link
                  </button>
                </div>

                {/* Permissions */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Permissions</label>
                    <button
                      onClick={() => setShowPermissionModal(true)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      + Add User
                    </button>
                  </div>
                  {selectedDashboard.permissions.length === 0 ? (
                    <p className="text-sm text-gray-500">No users added yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedDashboard.permissions.map((perm) => (
                        <div key={perm.user_id} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                          <div>
                            <span className="font-medium">{perm.user_id}</span>
                            <span className="ml-2 badge badge-secondary">{perm.role}</span>
                          </div>
                          <button
                            onClick={() => removePermission(perm.user_id)}
                            className="text-red-500 text-sm hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Settings Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Settings</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Visibility: <span className="font-medium">{selectedDashboard.visibility}</span></div>
                    <div>Password: <span className="font-medium">{selectedDashboard.password_protected ? 'Yes' : 'No'}</span></div>
                    <div>Embed: <span className="font-medium">{selectedDashboard.embed_enabled ? 'Enabled' : 'Disabled'}</span></div>
                    <div>Views: <span className="font-medium">{selectedDashboard.view_count}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Embed Modal */}
      {showEmbedModal && embedCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Embed Dashboard</h3>
                <button onClick={() => setShowEmbedModal(false)} className="text-gray-500 hover:text-gray-700">
                  X
                </button>
              </div>

              <div className="space-y-4">
                {/* iframe Code */}
                <div>
                  <label className="block text-sm font-medium mb-2">Iframe Embed Code</label>
                  <div className="relative">
                    <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
                      {embedCode.iframe_code}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(embedCode.iframe_code, 'iframe')}
                      className="absolute top-2 right-2 btn btn-sm btn-outline"
                    >
                      {copiedField === 'iframe' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* Script Code */}
                <div>
                  <label className="block text-sm font-medium mb-2">JavaScript Embed (Advanced)</label>
                  <div className="relative">
                    <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
                      {embedCode.script_code}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(embedCode.script_code, 'script')}
                      className="absolute top-2 right-2 btn btn-sm btn-outline"
                    >
                      {copiedField === 'script' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* Direct URL */}
                <div>
                  <label className="block text-sm font-medium mb-2">Direct Embed URL</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      className="input flex-1 bg-gray-50"
                      value={embedCode.iframe_url}
                    />
                    <button
                      onClick={() => copyToClipboard(embedCode.iframe_url, 'url')}
                      className="btn btn-outline"
                    >
                      {copiedField === 'url' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add Permission</h3>
                <button onClick={() => setShowPermissionModal(false)} className="text-gray-500 hover:text-gray-700">
                  X
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">User ID / Email</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={permissionForm.user_id}
                    onChange={(e) => setPermissionForm({ ...permissionForm, user_id: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Role</label>
                  <select
                    className="input w-full"
                    value={permissionForm.role}
                    onChange={(e) => setPermissionForm({ ...permissionForm, role: e.target.value })}
                  >
                    <option value="viewer">Viewer - Can only view</option>
                    <option value="editor">Editor - Can modify</option>
                    <option value="admin">Admin - Full control</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <button onClick={addPermission} className="btn btn-primary" disabled={!permissionForm.user_id}>
                    Add Permission
                  </button>
                  <button onClick={() => setShowPermissionModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .btn {
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        .btn-primary {
          background: #3b82f6;
          color: white;
        }
        .btn-primary:hover {
          background: #2563eb;
        }
        .btn-primary:disabled {
          background: #93c5fd;
          cursor: not-allowed;
        }
        .btn-secondary {
          background: #6b7280;
          color: white;
        }
        .btn-secondary:hover {
          background: #4b5563;
        }
        .btn-outline {
          border: 1px solid #d1d5db;
          background: white;
        }
        .btn-outline:hover {
          background: #f3f4f6;
        }
        .btn-danger {
          background: #ef4444;
          color: white;
        }
        .btn-danger:hover {
          background: #dc2626;
        }
        .btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.875rem;
        }
        .input {
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
        }
        .input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
        .card {
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .badge {
          display: inline-flex;
          padding: 0.125rem 0.5rem;
          font-size: 0.75rem;
          border-radius: 9999px;
        }
        .badge-success {
          background: #d1fae5;
          color: #065f46;
        }
        .badge-info {
          background: #dbeafe;
          color: #1e40af;
        }
        .badge-warning {
          background: #fef3c7;
          color: #92400e;
        }
        .badge-secondary {
          background: #e5e7eb;
          color: #374151;
        }
      `}</style>
    </div>
  )
}
